import React from 'react';
import clsx from 'clsx';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { ListItemButton, ListItemText, Tooltip, styled, alpha, type ListItemButtonProps } from '@mui/material';
import NavLinkAdapter from '@fuse/core/NavLinkAdapter';
import { Icon } from 'app/main/components';
import FuseNavBadge from '../../FuseNavBadge';
import type { FuseNavTabProps } from '../../types/FuseNavProps';

const Root = styled('div')(({ theme }) => ({
	'& > .fuse-list-item': {
		minHeight: 100,
		height: 100,
		width: 100,
		borderRadius: 12,
		margin: '0 0 4px 0',
		color: alpha(theme.palette.text.primary, 0.7),
		cursor: 'pointer',
		textDecoration: 'none!important',
		padding: 0,
		'&.dense': {
			minHeight: 52,
			height: 52,
			width: 52
		},
		'&.type-divider': {
			padding: 0,
			height: 2,
			minHeight: 2,
			margin: '12px 0',
			backgroundColor:
				theme.palette.mode === 'light' ? 'rgba(0, 0, 0, .05)!important' : 'rgba(255, 255, 255, .1)!important',
			pointerEvents: 'none'
		},
		'&:hover': {
			color: theme.palette.text.primary
		},
		'&.active': {
			color: theme.palette.text.primary,
			backgroundColor:
				theme.palette.mode === 'light' ? 'rgba(0, 0, 0, .05)!important' : 'rgba(255, 255, 255, .1)!important',
			// pointerEvents: 'none',
			transition: 'border-radius .15s cubic-bezier(0.4,0.0,0.2,1)',
			'& .fuse-list-item-text-primary': {
				color: 'inherit'
			},
			'& .fuse-list-item-icon': {
				color: 'inherit'
			}
		},
		'& .fuse-list-item-icon': {
			color: 'inherit'
		},
		'& .fuse-list-item-text': {}
	}
}));

interface StyledListItemButtonProps extends ListItemButtonProps {
	to?: string;
	end?: boolean;
}
const StyledListItemButton = styled(ListItemButton)<StyledListItemButtonProps>({});

function FuseNavVerticalTab(props: FuseNavTabProps) {
	const dispatch = useDispatch();
	const location = useLocation();

	const { item, onItemClick, firstLevel, dense, selectedId } = props;

	return React.useMemo(
		() => (
			<Root>
				<StyledListItemButton
					component={item.url ? NavLinkAdapter : 'button'}
					to={item.url}
					className={clsx(
						`type-${item.type}`,
						dense && 'dense',
						selectedId === item.id && 'active',
						'fuse-list-item flex flex-col items-center justify-center p-12'
					)}
					onClick={() => onItemClick && onItemClick(item)}
					end={item.end}
					role="button"
				>
					{dense ? (
						<Tooltip title={item.title || ''} placement="right">
							<div className="relative flex h-32 min-h-32 w-32 items-center justify-center">
								{item.icon ? (
									<Icon
										icon={item.icon}
										className={clsx('fuse-list-item-icon text-24', item.iconClass)}
										color="action"
									/>
								) : (
									item.title && <div className="text-16 font-bold">{item.title[0]}</div>
								)}
								{item.badge && (
									<FuseNavBadge
										badge={item.badge}
										className="absolute top-0 h-16 min-w-16 justify-center p-4 ltr:right-0 rtl:left-0"
									/>
								)}
							</div>
						</Tooltip>
					) : (
						<>
							<div className="relative mb-8 flex h-32 min-h-32 w-32 items-center justify-center">
								{item.icon ? (
									<Icon
										icon={item.icon}
										className={clsx('fuse-list-item-icon text-32', item.iconClass)}
										color="action"
									/>
								) : (
									item.title && <div className="text-20 font-bold">{item.title[0]}</div>
								)}
								{item.badge && (
									<FuseNavBadge
										badge={item.badge}
										className="absolute top-0 h-16 min-w-16 justify-center p-4 ltr:right-0 rtl:left-0"
									/>
								)}
							</div>

							<ListItemText
								className="fuse-list-item-text m-0 w-full grow-0"
								primary={item.title}
								classes={{
									primary: 'text-12 font-medium fuse-list-item-text-primary truncate text-center'
								}}
							/>
						</>
					)}
				</StyledListItemButton>
				{!firstLevel &&
					item.children &&
					item.children.map(_item => (
						<NavVerticalTab
							key={_item.id}
							item={_item}
							onItemClick={onItemClick}
							dense={dense}
							selectedId={selectedId}
						/>
					))}
			</Root>
		),
		[firstLevel, item, onItemClick, dense, selectedId]
	);
}

const NavVerticalTab = FuseNavVerticalTab;

export default NavVerticalTab;
